import { V1CloudEventsInsightCategory } from '@ariksa/audittrail/api';
import { Resources } from '@ariksa/inventory-core/api';
import { Box, Center, HStack, Stack } from '@chakra-ui/layout';
import { includes, map, some } from 'lodash';
import { components as selectComponents } from 'react-select';
import { colorOpacity, customTheme } from 'theme';

import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import {
  AriksaIcon,
  ButtonGroupItem,
  formatTooltip,
  getIconColor,
  IconTypes,
  renderTooltipFieldValue,
  StackedCell,
  Tag,
} from 'components/index';
import { toTitleCase } from 'utils/string';

import { CloudEventsViewByTabs } from '../types';

export const cloudEventsViewByItems: ButtonGroupItem[] = map(
  CloudEventsViewByTabs,
  o => ({
    label: o,
    value: o,
    isDisabled:
      o === CloudEventsViewByTabs.Identity ||
      o === CloudEventsViewByTabs.Network,
  }),
);

export const cloudEventsIdentityTypeOptions = [
  { label: 'Users', value: Resources.User },
  { label: 'Virtual Machines', value: Resources.VirtualMachine },
  { label: 'Serverless Functions', value: Resources.ServerlessCompute },
  { label: 'AI Services', value: 'AiServices' },
];

export const renderRequestCount = (count, color) => {
  return (
    <Tag
      label={formatNumber(count)}
      styles={{
        tag: {
          bg: colorOpacity(color, 0.1),
          borderRadius: 12,
          border: 'none',
          px: 3,
        },
        label: { color, fontWeight: 600 },
      }}
    />
  );
};

const renderIcon = (type, list = [], count = 0) => {
  const color = getIconColor(type);
  const title =
    type === V1CloudEventsInsightCategory.Malicious ? 'Malicious' : 'Anomalous';
  return (
    <AriksaIcon
      type={type}
      size="sm"
      circle
      color={color}
      bg={colorOpacity(color, 0.1)}
      useCustomColor
      border={false}
      tooltip={
        list
          ? formatTooltip({
              header: title,
              content: (
                <Stack spacing={2}>
                  {renderTooltipFieldValue('Events Count', count)}
                  <Stack spacing={0}>
                    <Box color="orange">Insight Types</Box>
                    {map(list, (o, index) => (
                      <Box>
                        {index + 1}. {o}
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              ),
              width: 56,
            })
          : title
      }
    />
  );
};

export const renderCloudEventsInsights = ({ row }) => {
  const melicious = parseInt(row?.malicious_events);
  const anomalous = parseInt(row?.anomalous_events);

  if (!melicious && !anomalous) return '-';

  return (
    <Center>
      <HStack>
        {!!melicious &&
          renderIcon(
            V1CloudEventsInsightCategory.Malicious,
            row?.malicious_insight_types,
            row?.malicious_events,
          )}
        {!!anomalous &&
          renderIcon(
            V1CloudEventsInsightCategory.Anomalous,
            row?.anomalous_insight_types,
            row?.anomalous_events,
          )}
      </HStack>
    </Center>
  );
};

export const renderCloudEventsInsightsForResource = ({ row }) => {
  return (
    <Center>
      <HStack>{map(row?.insights, o => renderIcon(o))}</HStack>
    </Center>
  );
};

export const insightCategoryOptions = [
  { label: 'Anomaly', value: V1CloudEventsInsightCategory.Anomalous },
  { label: 'Malicious', value: V1CloudEventsInsightCategory.Malicious },
];

const countryCodeAliases: Record<string, string> = {
  BEL: 'BE',
  BELGIUM: 'BE',
};

const getCountryCode = (countryCode?: String) => {
  const code = countryCode?.toString().trim().toUpperCase();

  if (!code) return undefined;
  if (/^[A-Z]{2}$/.test(code)) return code;

  return countryCodeAliases[code];
};

const isAwsSourceIp = (sourceIp?: String) => {
  const normalizedSourceIp = sourceIp
    ?.toString()
    .trim()
    .toLowerCase()
    ?.split('.');
  console.log(normalizedSourceIp);
  return some(normalizedSourceIp, o =>
    includes(
      [
        'amazonaws',
        'aws internal',
        'delivery.logs',
        'delivery.logs.amazonaws.com',
      ],
      o,
    ),
  );
};

export const renderSourceIP = ({ row }) => {
  const isAwsSource = isAwsSourceIp(row?.source_ip);
  const countryCode =
    getCountryCode(row?.source_country_code) ??
    getCountryCode(row?.source_country);

  return (
    <StackedCell
      upper={row.source_ip}
      lower={
        !row.source_city && !row.source_country
          ? '-'
          : row.source_city +
            (row?.source_country ? ', ' : '') +
            row?.source_country
      }
      iconProps={
        isAwsSource
          ? {
              resourceType: IconTypes.AWS,
              iconStyles: { w: 5, h: 5 },
              iconFilled: false,
            }
          : countryCode
          ? {
              icon: (
                <Box
                  as="img"
                  src={`https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`}
                  alt={row?.source_country?.toString() || countryCode}
                  w="20px"
                  h="13px"
                  objectFit="cover"
                  borderRadius="1px"
                />
              ),
              iconStyles: { w: 5, h: 5 },
              iconFilled: false,
            }
          : {
              resourceType: IconTypes.Globe,
              iconStyles: { w: 5, h: 5 },
              iconFilled: false,
              color: customTheme.colors.critical,
              useCustomColor: true,
            }
      }
    />
  );
};

export const ObjectPathMenuList = props => (
  <selectComponents.MenuList {...props}>
    <Box
      color={customTheme.colors.gray['300']}
      pb={2}
      mb={2}
      borderBottom="1px solid"
      borderColor="gray.200"
      fontSize="sm"
    >
      OBJECT PATHS
    </Box>
    {props.children}
  </selectComponents.MenuList>
);
